import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation, Trans } from 'react-i18next';
import { Header } from '@/components/Header';
import { CollapsibleText } from '@/components/CollapsibleText';
import { SEO, breadcrumbJsonLd } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MapPin, Users, Clock, Car, Search, ChevronDown } from 'lucide-react';
import { pickTranslation } from '@/i18n/pickTranslation';
import { numberLocale } from '@/lib/format';
import { depositPercentage, hasNoDeposit } from '@/lib/noDeposit';
import { currentUrlLang, pathInLang } from '@/i18n/urlLanguage';
import { publicProviderName } from '@/lib/providerName';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Duration options — labels resolved via useTranslation in the component.
const DURATION_OPTIONS = [
  { value: 4, labelKey: 'transport.durationOptions.h4' },
  { value: 8, labelKey: 'transport.durationOptions.h8' },
  { value: 12, labelKey: 'transport.durationOptions.day12' },
];

// Transport is 24/7 — full-day slots in 30-minute steps.
const TRANSPORT_HOUR_SLOTS = (() => {
  const out = [];
  for (let h = 0; h < 24; h++) {
    out.push(`${String(h).padStart(2, '0')}:00`);
    out.push(`${String(h).padStart(2, '0')}:30`);
  }
  return out;
})();

const todayYmd = () => new Date().toISOString().split('T')[0];

// Small reusable widget — Calendar popover + 24h time Select — reused twice
// (Transfer and Private Driver tabs).
const TransportDateTime = ({ date, time, onDate, onTime, testidPrefix }) => {
  const { t } = useTranslation();
  return (
  <>
    <div>
      <Label className="text-xs font-semibold">{t('transport.search.date')}</Label>
      <input
        type="date"
        value={date}
        onChange={(e) => onDate(e.target.value)}
        className="w-full bg-background-soft border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-primary focus:border-accent focus:outline-none"
        data-testid={`${testidPrefix}-date`}
      />
    </div>
    <div>
      <Label className="text-xs font-semibold">{t('transport.search.time')}</Label>
      <div className="relative">
        <select
          value={time}
          onChange={(e) => onTime(e.target.value)}
          className="w-full appearance-none bg-background-soft border border-slate-200 rounded-xl px-3 py-2.5 pr-8 text-sm text-primary focus:border-accent focus:outline-none"
          data-testid={`${testidPrefix}-time`}
        >
          {TRANSPORT_HOUR_SLOTS.map((h) => (
            <option key={h} value={h}>{h}</option>
          ))}
        </select>
        <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
      </div>
    </div>
  </>
  );
};

export const TransportMarketplace = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [zones, setZones] = useState([]);
  const [tab, setTab] = useState(searchParams.get('tab') === 'private_driver' ? 'private_driver' : 'transfer');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  // Transfer form
  const [fromZone, setFromZone] = useState(searchParams.get('from') || '');
  const [toZone, setToZone] = useState(searchParams.get('to') || '');
  const [date, setDateValue] = useState(searchParams.get('date') || todayYmd());
  const [time, setTimeValue] = useState(searchParams.get('time') || '10:00');
  // The date / time defaults (today, 10:00) are only form defaults: they are forwarded to the offer page ONLY when the
  // traveller gave them (link or typing). Otherwise the booking form starts empty instead of silently getting them.
  const [dateGiven, setDateGiven] = useState(!!searchParams.get('date'));
  const [timeGiven, setTimeGiven] = useState(!!searchParams.get('time'));
  const setDate = (v) => { setDateValue(v); setDateGiven(!!v); };
  const setTime = (v) => { setTimeValue(v); setTimeGiven(!!v); };
  const [passengers, setPassengers] = useState(parseInt(searchParams.get('pax') || '2'));

  // Private driver form (share date/time/passengers with transfer)
  const [duration, setDuration] = useState(parseInt(searchParams.get('duration') || '8'));

  useEffect(() => {
    axios.get(`${API}/zones`).then((r) => setZones(r.data || [])).catch(() => setZones([]));
  }, []);

  // Home → Transport should land directly on the available offers instead of
  // forcing the traveler through an intermediate search screen. Perform an
  // unfiltered fetch (respecting the current tab) as soon as the page mounts.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setSearched(true);
      try {
        const params = new URLSearchParams();
        params.set('category', tab);
        // On initial mount, only forward duration if the user explicitly set it
        // via URL (?duration=). Otherwise we want the marketplace to display
        // "À partir de <min>" instead of a duration-specific matched_price.
        if (tab === 'private_driver' && searchParams.get('duration')) {
          params.set('duration_hours', String(duration));
        }
        const { data } = await axios.get(`${API}/services?${params.toString()}`);
        if (!cancelled) setResults(data || []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const search = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    setSearched(true);
    try {
      const params = new URLSearchParams();
      params.set('category', tab);
      params.set('passengers_min', String(passengers || 1));

      if (tab === 'transfer') {
        if (fromZone) params.set('from_zone_id', fromZone);
        if (toZone) params.set('to_zone_id', toZone);
      } else {
        // Private Driver V1 — add availability window for conflict check.
        // Calculate end_time = start_time + duration_hours to filter overlapping bookings.
        params.set('duration_hours', String(duration));
        if (date && time) {
          try {
            const [h, m] = time.split(':').map(Number);
            const startDt = new Date(`${date}T00:00:00`);
            startDt.setHours(h, m, 0, 0);
            const endDt = new Date(startDt.getTime() + duration * 60 * 60 * 1000);

            params.set('start_date', date);
            params.set('end_date', endDt.toISOString().split('T')[0]);
            params.set('start_time', time);
            params.set('end_time', `${String(endDt.getHours()).padStart(2, '0')}:${String(endDt.getMinutes()).padStart(2, '0')}`);
          } catch {
            // Fallback: skip availability filter if date/time parsing fails.
          }
        }
      }

      const { data } = await axios.get(`${API}/services?${params.toString()}`);
      setResults(data || []);
      // Persist search state to URL for shareability
      const urlParams = new URLSearchParams();
      urlParams.set('tab', tab);
      if (tab === 'transfer') {
        if (fromZone) urlParams.set('from', fromZone);
        if (toZone) urlParams.set('to', toZone);
      } else {
        urlParams.set('duration', String(duration));
      }
      urlParams.set('date', date);
      urlParams.set('time', time);
      urlParams.set('pax', String(passengers));
      setSearchParams(urlParams, { replace: true });
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const openOffer = (serviceId) => {
    const q = new URLSearchParams();
    if (dateGiven && date) q.set('date', date);
    if (timeGiven && time) q.set('time', time);
    q.set('pax', String(passengers));
    if (tab === 'transfer') {
      if (fromZone) q.set('from_zone_id', fromZone);
      if (toZone) q.set('to_zone_id', toZone);
    } else if (tab === 'private_driver') {
      q.set('duration', String(duration));
    }
    navigate(`/services/${serviceId}?${q.toString()}`);
  };

  const zonesById = useMemo(() => Object.fromEntries(zones.map((z) => [z.zone_id, z])), [zones]);

  return (
    <div className="min-h-screen bg-background" data-testid="transport-marketplace">
      <SEO
        title={t('seo.transport.title')}
        description={t('seo.transport.description')}
        canonical="https://ridalo.co/transport"
        jsonLd={breadcrumbJsonLd([
          { name: t('seo.breadcrumb.home'), path: '/' },
          { name: t('seo.breadcrumb.transport'), path: '/transport' },
        ])}
      />
      <Header />
      <div className="pt-20 md:pt-28 pb-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile (< md): title, the two category buttons, then the offers straight away; the long SEO text comes
              AFTER the offers (still in the page, visible). Desktop keeps the order it always had. */}
          <div className="flex flex-col">
            <div className="text-center">
              <h1 className="text-2xl sm:text-4xl font-bold tracking-tight text-primary mb-3 sm:mb-4 md:mb-1">
                {t('transport.mobileTitle')}
              </h1>
              <p className="hidden md:block text-base text-ink-light mb-5" data-testid="transport-subtitle">{t('transport.pageSubtitle')}</p>
            </div>
            {/* SEO text. Desktop: as it always was. Phones: a compact block after the offers — a small heading, ONE line of
                the real text (title phrase + paragraph + airport link stay in the page's HTML) and « Read more », which
                opens the whole text (with the link) in a dialog. */}
            <div className="order-last mt-6 mb-0 text-left md:mt-10 md:rounded-2xl md:border md:border-slate-200 md:bg-white md:p-6" data-testid="transport-intro">
              <h2 className="md:hidden text-sm font-semibold text-primary">{t('transport.mobileInfoTitle')}</h2>
              <h2 className="hidden md:block text-xl font-semibold text-primary mb-2">{t('transport.introTitle')}</h2>
              <CollapsibleText title={t('transport.mobileInfoTitle')} testId="transport-intro-text">
                <span className="md:hidden">{t('transport.introTitle')}. </span>
                <p className="text-base sm:text-lg text-ink-light max-w-3xl mx-auto max-md:inline md:text-base md:mx-0">
                  <Trans i18nKey="transport.introText" components={{ strong: <strong /> }} />
                </p>
                <Link
                  to={pathInLang(currentUrlLang() || 'fr', '/transfert-aeroport-phuket')}
                  className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-accent hover:underline max-sm:min-h-11 max-md:mt-0 max-md:ml-1"
                  data-testid="transport-airport-page-link"
                >
                  <MapPin className="w-4 h-4 shrink-0" aria-hidden="true" />
                  {t('transport.airportPageLink')}
                </Link>
              </CollapsibleText>
            </div>

            {/* Header — the second big title is not shown on mobile */}
            <div className="mb-6 hidden">
              <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-primary">{t('transport.pageTitle')}</h2>
              <p className="text-base text-ink-light mt-2">
                {t('transport.pageSubtitle')}
              </p>
            </div>

            {/* Tabs */}
            <div className="grid grid-cols-2 gap-2 mb-4 md:flex md:justify-center md:mb-6" data-testid="transport-tabs">
              <button
                type="button"
                className={`px-3 md:px-5 py-2.5 max-md:min-h-11 rounded-full text-sm font-medium border transition-colors ${tab === 'transfer' ? 'bg-accent text-white border-accent' : 'bg-white text-primary border-slate-200 hover:border-accent'}`}
                onClick={() => { setTab('transfer'); setResults([]); setSearched(false); }}
                data-testid="tab-transfer"
              >
                <MapPin className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
                {t('transport.tabs.transfer')}
              </button>
              <button
                type="button"
                className={`px-3 md:px-5 py-2.5 max-md:min-h-11 rounded-full text-sm font-medium border transition-colors ${tab === 'private_driver' ? 'bg-accent text-white border-accent' : 'bg-white text-primary border-slate-200 hover:border-accent'}`}
                onClick={() => { setTab('private_driver'); setResults([]); setSearched(false); }}
                data-testid="tab-private-driver"
              >
                <Users className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
                {t('transport.tabs.privateDriver')}
              </button>
            </div>

          {/* Search bar */}
          {/* Intermediate search: hidden on every screen — one provider today, and the vehicle's booking form asks for the
                route, date, time, passengers, luggage / flight / duration. Kept (and wired) for a future reactivation
                with several providers. */}
            <form onSubmit={search} className="card-dashboard mb-8 hidden" data-testid="transport-search-form">
            {tab === 'transfer' ? (
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                <div>
                  <Label className="text-xs font-semibold">{t('transport.search.from')}</Label>
                  <div className="relative">
                    <select
                      value={fromZone}
                      onChange={(e) => setFromZone(e.target.value)}
                      className="w-full appearance-none bg-background-soft border border-slate-200 rounded-xl px-3 py-2.5 pr-8 text-sm font-medium text-primary focus:border-accent focus:outline-none"
                      data-testid="search-from"
                    >
                      <option value="">{t('transport.search.fromPlaceholder')}</option>
                      {zones.map((z) => (
                        <option key={z.zone_id} value={z.zone_id}>{z.name}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold">{t('transport.search.to')}</Label>
                  <div className="relative">
                    <select
                      value={toZone}
                      onChange={(e) => setToZone(e.target.value)}
                      className="w-full appearance-none bg-background-soft border border-slate-200 rounded-xl px-3 py-2.5 pr-8 text-sm font-medium text-primary focus:border-accent focus:outline-none"
                      data-testid="search-to"
                    >
                      <option value="">{t('transport.search.toPlaceholder')}</option>
                      {zones.map((z) => (
                        <option key={z.zone_id} value={z.zone_id}>{z.name}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>
                <TransportDateTime date={date} time={time} onDate={setDate} onTime={setTime} testidPrefix="search" />
                <div>
                  <Label className="text-xs font-semibold">{t('transport.search.passengers')}</Label>
                  <Input type="number" min="1" max="20" value={passengers} onChange={(e) => setPassengers(parseInt(e.target.value) || 1)} data-testid="search-pax" />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <TransportDateTime date={date} time={time} onDate={setDate} onTime={setTime} testidPrefix="search-pd" />
                <div>
                  <Label className="text-xs font-semibold">{t('transport.search.duration')}</Label>
                  <div className="relative">
                    <select
                      value={String(duration)}
                      onChange={(e) => setDuration(parseInt(e.target.value))}
                      className="w-full appearance-none bg-background-soft border border-slate-200 rounded-xl px-3 py-2.5 pr-8 text-sm font-medium text-primary focus:border-accent focus:outline-none"
                      data-testid="search-duration"
                    >
                      {DURATION_OPTIONS.map((d) => (
                        <option key={d.value} value={String(d.value)}>{t(d.labelKey)}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold">{t('transport.search.passengers')}</Label>
                  <Input type="number" min="1" max="20" value={passengers} onChange={(e) => setPassengers(parseInt(e.target.value) || 1)} data-testid="search-pax-pd" />
                </div>
              </div>
            )}
            <div className="mt-4 flex justify-end">
              <Button type="submit" className="bg-accent text-white hover:opacity-90" disabled={loading} data-testid="transport-search-submit">
                <Search className="w-4 h-4 mr-1.5" /> {loading ? t('transport.search.submitting') : t('transport.search.submit')}
              </Button>
            </div>
          </form>

          {/* Results */}
          {searched && (
            <div data-testid="transport-results">
              {loading ? (
                <p className="text-center text-ink-muted py-12">{t('transport.results.loading')}</p>
              ) : results.length === 0 ? (
                <div className="card-dashboard text-center py-12">
                  <Car className="w-10 h-10 text-accent mx-auto mb-3" />
                  <p className="text-primary font-semibold mb-1">{t('transport.results.emptyTitle')}</p>
                  <p className="text-sm text-ink-muted">{t('transport.results.emptyBody')}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" data-testid="transport-grid">
                  {results.map((s) => (
                    <TransportResultCard
                      key={s.service_id}
                      service={s}
                      zonesById={zonesById}
                      fromZone={tab === 'transfer' ? fromZone : null}
                      toZone={tab === 'transfer' ? toZone : null}
                      onOpen={() => openOffer(s.service_id)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

const PHUKET_AIRPORT_ZONE_ID = 'zone_phuket_airport';

const TransportResultCard = ({ service, zonesById, onOpen, fromZone, toZone }) => {
  const { t, i18n } = useTranslation();
  const localizedTitle = pickTranslation(service, 'title', i18n.language) || service.title;
  const transport = ((service.details || {}).transport) || {};
  const vehicle = transport.vehicle || {};
  const category = service.category;
  const isTransfer = category === 'transfer';

  // Airport transfer V1: derive route from search context if available.
  let fromLabel = null;
  let toLabel = null;
  if (isTransfer && fromZone && toZone) {
    fromLabel = zonesById[fromZone]?.name || null;
    toLabel = zonesById[toZone]?.name || null;
  } else if (isTransfer && (transport.airport_transfers || []).length > 0) {
    // No specific route in search context — show the airport as origin and
    // list up to two destination zones served.
    const dests = (transport.airport_transfers || [])
      .map((t) => zonesById[t.zone_id]?.name)
      .filter(Boolean)
      .slice(0, 2);
    fromLabel = 'Phuket Airport';
    toLabel = dests.length ? dests.join(', ') + (transport.airport_transfers.length > 2 ? '…' : '') : null;
  } else {
    // Legacy transfer or private_driver fallback (custom_from/to)
    fromLabel = transport.custom_from || transport.from_zone_name || (zonesById[transport.from_zone_id]?.name) || null;
    toLabel = transport.custom_to || transport.to_zone_name || (zonesById[transport.to_zone_id]?.name) || null;
  }

  // Price display:
  //   - Airport Transfers: if the user searched for a specific route, backend returns
  //     `matched_price` → show that exact price. Otherwise, show "À partir de <min>".
  //   - Private Driver V1: if the user searched for a specific duration, backend returns
  //     `matched_price` for that duration. Otherwise, show "À partir de <min>" computed from
  //     all enabled durations.
  const isPrivateDriver = category === 'private_driver';
  const airportTransfers = transport.airport_transfers || [];
  const airportMinPrice = airportTransfers.length > 0
    ? Math.min(...airportTransfers.map((t) => Number(t.price)).filter((p) => Number.isFinite(p) && p > 0))
    : null;
  const privateDriverDurations = isPrivateDriver ? (transport.durations || []) : [];
  const privateDriverMinPrice = privateDriverDurations.length > 0
    ? Math.min(...privateDriverDurations.map((d) => Number(d.price)).filter((p) => Number.isFinite(p) && p > 0))
    : null;
  let displayedPrice;
  let priceLabel = null;
  if (service.matched_price != null) {
    // Backend attached matched_price (Transfer route or Private Driver duration)
    displayedPrice = service.matched_price;
  } else if (isTransfer && airportMinPrice != null && Number.isFinite(airportMinPrice)) {
    displayedPrice = airportMinPrice;
    priceLabel = t('transport.results.startingFrom');
  } else if (isPrivateDriver && privateDriverMinPrice != null && Number.isFinite(privateDriverMinPrice)) {
    displayedPrice = privateDriverMinPrice;
    priceLabel = t('transport.results.startingFrom');
  } else {
    displayedPrice = service.price;
  }
  // From the server's deposit rule (the one the booking applies), never assumed.
  const showNoDeposit = hasNoDeposit(service);
  const depositPct = depositPercentage(service);

  return (
    <div
      onClick={onOpen}
      className="group card-offer overflow-hidden cursor-pointer flex flex-col"
      data-testid={`result-${service.service_id}`}
    >
      {/* Cover image */}
      <div className="relative h-52 sm:h-56 overflow-hidden bg-slate-100">
        {service.images && service.images[0] ? (
          <img
            src={service.images[0]}
            alt={localizedTitle}
            width="640" height="416"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-accent/10 to-accent/5">
            <Car className="w-12 h-12 text-accent/60 mb-2" />
            <span className="text-xs font-medium text-accent/70">{t('transport.results.transportFallback')}</span>
          </div>
        )}
        {/* Category badge (top-left) */}
        <div className="absolute top-3 left-3 inline-flex items-center gap-1 bg-white/95 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-semibold text-primary shadow-sm">
          {isTransfer ? (
            <><MapPin className="w-3.5 h-3.5 text-accent" /> {t('transport.results.transferBadge')}</>
          ) : (
            <><Users className="w-3.5 h-3.5 text-accent" /> {t('transport.results.privateDriverBadge')}</>
          )}
        </div>
        {/* Deposit badge (top-right) */}
        {showNoDeposit && (
          <div className="absolute top-3 right-3 inline-flex items-center gap-1 bg-emerald-500 text-white rounded-full px-3 py-1 text-xs font-semibold shadow-sm" data-testid={`no-deposit-badge-${service.service_id}`}>
            {t('transport.results.noDeposit')}
          </div>
        )}
        {depositPct && (
          <div className="absolute top-3 right-3 inline-flex items-center gap-1 bg-amber-500 text-white rounded-full px-3 py-1 text-xs font-semibold shadow-sm" data-testid={`deposit-badge-${service.service_id}`}>
            {t('transport.results.depositPct', { pct: depositPct })}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-5 flex flex-col flex-1">
        <h3 className="text-lg font-semibold text-primary leading-tight line-clamp-2 mb-1">
          {localizedTitle}
        </h3>
        <p className="text-xs text-ink-muted mb-3">{t('transport.results.providerPrefix', { name: publicProviderName(service) })}</p>

        {/* Route / info chips */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {fromLabel && toLabel && (
            <span className="inline-flex items-center gap-1 bg-background-soft text-primary text-xs font-medium rounded-full px-2.5 py-1">
              <MapPin className="w-3 h-3 text-accent" /> {fromLabel} → {toLabel}
            </span>
          )}
          {vehicle.passengers_max && (
            <span className="inline-flex items-center gap-1 bg-background-soft text-primary text-xs font-medium rounded-full px-2.5 py-1">
              <Users className="w-3 h-3 text-accent" /> {t('transport.results.paxShort', { count: vehicle.passengers_max })}
            </span>
          )}
          {(vehicle.brand || vehicle.vehicle_type_label) && (
            <span className="inline-flex items-center gap-1 bg-background-soft text-primary text-xs font-medium rounded-full px-2.5 py-1">
              <Car className="w-3 h-3 text-accent" /> {[vehicle.vehicle_type_label, vehicle.brand, vehicle.model].filter(Boolean).join(' · ')}
            </span>
          )}
          {!isTransfer && transport.duration_hours && (
            <span className="inline-flex items-center gap-1 bg-background-soft text-primary text-xs font-medium rounded-full px-2.5 py-1">
              <Clock className="w-3 h-3 text-accent" /> {transport.duration_hours} h
            </span>
          )}
        </div>

        {/* Price + CTA (bottom, always aligned) */}
        <div className="flex items-end justify-between pt-3 border-t border-slate-100 mt-auto">
          <div>
            {priceLabel && (
              <p className="text-xs text-ink-muted uppercase tracking-wide leading-none mb-0.5" data-testid={`starting-label-${service.service_id}`}>{priceLabel}</p>
            )}
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold text-primary" data-testid={`price-${service.service_id}`}>
                {Number(displayedPrice).toLocaleString(numberLocale())}
              </span>
              <span className="text-sm text-ink-light">THB</span>
            </div>
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpen(); }}
            className="text-sm font-semibold text-accent group-hover:text-accent-hover transition-colors max-sm:min-h-11 max-sm:px-3 max-sm:-mr-3"
            data-testid={`result-book-${service.service_id}`}
          >
            {t('transport.results.viewOffer')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransportMarketplace;
